import type { CSSInterpolation } from '@emotion/serialize';
import type React from 'react';

import type { Styling } from 'src/context/styling';
import {
  BackgroundTheme,
  BannerTextBackgroundTheme,
  HighlightsBackgroundTheme,
  IconTheme,
  TextBackgroundTheme,
  TrialBackgroundTheme,
} from 'src/enums/background-theme';
import { BuildType } from 'src/enums/build/build-type';
import type {
  RedesignHarbourButtonStyle,
  RedesignMeridianButtonStyle,
} from 'src/enums/button-style';
import { ButtonsArrangement, ButtonStyle, RedesignButtonStyle } from 'src/enums/button-style';
import { ImageSize } from 'src/enums/image-size';
import { ProductBannerImageWithTextStyle } from 'src/enums/product-banner-image-with-text-style';
import { TextOverPosition } from 'src/enums/text-over-position';
import { TitleFontColour } from 'src/enums/title-font-colour';
import { WidgetTheme } from 'src/enums/widget-theme';
import imageFade from 'src/helpers/image-fades';
import { colors } from 'src/styling/constants';

import type { GeneralRedesignButtonStyle } from '../../cms/common-widgets/button-widget';

export const rgbaValues = (hex: string, alpha = 1) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  // We convert the first 2 hex characters to the r value, the next 2 characters to g and the last 2 characters to b
  return result
    ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`
    : undefined;
};

type BackgroundThemeClassNames = Record<string, string>;

export const getBackgroundThemeClassName = (
  theme:
    | BackgroundTheme
    | HighlightsBackgroundTheme
    | TrialBackgroundTheme
    | string
    | Queries.Maybe<string>
    | undefined,
  styles: BackgroundThemeClassNames
) => {
  switch (theme) {
    case BackgroundTheme.GREY:
      return styles.grey;
    case BackgroundTheme.PALE_GOLD:
      return styles.paleGold;
    case HighlightsBackgroundTheme.LIGHT_GREEN:
      return styles.lightGreen;
    case TrialBackgroundTheme.WHITE:
      return styles.white;
    case BackgroundTheme.LIGHT:
    default:
      return undefined;
  }
};

export const getBackgroundColorByTheme = (
  styling: Styling,
  theme:
    | BackgroundTheme
    | HighlightsBackgroundTheme
    | TrialBackgroundTheme
    | string
    | Queries.Maybe<string>
    | undefined,
  buildType: BuildType,
  alpha?: number
): string | undefined => {
  const isHarbour = buildType === BuildType.HARBOUR;
  switch (theme) {
    case BackgroundTheme.GREY:
      return rgbaValues(
        isHarbour ? styling.colors.davidLloydGray : styling.colors.greyBackground,
        alpha
      );
    case BackgroundTheme.PALE_GOLD:
      return rgbaValues(styling.colors.paleGold, alpha);
    case HighlightsBackgroundTheme.LIGHT_GREEN:
      return styling.colors.lightGreen;
    case HighlightsBackgroundTheme.PALE_GOLD:
      return styling.colors.paleGold;
    case TrialBackgroundTheme.WHITE:
      return 'white';
    case BackgroundTheme.LIGHT:
    default:
      return rgbaValues(isHarbour ? '#FFFFFF' : styling.colors.lightBackground, alpha);
  }
};

export const getReverseBackgroundColorByTheme = (
  styling: Styling,
  theme: BackgroundTheme | string | null,
  buildType: BuildType
): string => {
  const isHarbour = buildType === BuildType.HARBOUR;
  switch (theme) {
    case BackgroundTheme.GREY:
      return isHarbour ? '#FFFFFF' : styling.colors.lightBackground;
    case BackgroundTheme.PALE_GOLD:
      return styling.colors.paleGold;
    case BackgroundTheme.LIGHT:
    default:
      return isHarbour ? styling.colors.davidLloydGray : styling.colors.greyBackground;
  }
};

export const getTextBackground = (styling: Styling, theme: TextBackgroundTheme): string[] => {
  switch (theme) {
    case TextBackgroundTheme.GREY:
      return [styling.colors.greyBackground, styling.colors.greyBackground];
    case TextBackgroundTheme.LIGHT:
      return ['white', 'white'];
    default:
      return ['white', styling.colors.greyBackground];
  }
};

export const getFlexPositioning = (position: TextOverPosition) => {
  switch (position) {
    case TextOverPosition.LEFT:
    case TextOverPosition.CENTER_LEFT:
      return {
        alignItems: ['flex-end', 'center'],
        justifyContent: 'flex-start',
      };
    case TextOverPosition.CENTER:
      return {
        alignItems: ['flex-end', 'center'],
        justifyContent: 'center',
      };
    case TextOverPosition.BOTTOM_RIGHT:
      return {
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
      };
    case TextOverPosition.BOTTOM_LEFT:
      return {
        alignItems: 'flex-end',
        justifyContent: 'flex-start',
      };
    case TextOverPosition.RIGHT:
    case TextOverPosition.CENTER_RIGHT:
    default:
      return {
        alignItems: ['flex-end', 'center'],
        justifyContent: 'flex-end',
      };
  }
};

export const getImageFade = (position: TextOverPosition) => {
  switch (position?.toLowerCase() as TextOverPosition) {
    case TextOverPosition.LEFT:
    case TextOverPosition.CENTER_LEFT:
      return imageFade.centerLeft;
    case TextOverPosition.BOTTOM_RIGHT:
      return imageFade.bottomRight;
    case TextOverPosition.BOTTOM_LEFT:
      return imageFade.bottomLeft;
    case TextOverPosition.RIGHT:
    case TextOverPosition.CENTER_RIGHT:
      return imageFade.centerRight;
    case TextOverPosition.CENTER:
    default:
      return imageFade.bottomFade;
  }
};

export const markdownBulletStyle = (styling: Styling, color?: string, withLinkStyle = true) => {
  const bulletPoint = '"\u2022"';

  const markdownListStyle = (styling: Styling) =>
    [
      styling.fonts.regular,
      {
        paddingLeft: '1.5rem',
        margin: 'auto',
        textAlign: 'left',
        color: color || styling.colors.capeCod,
        lineHeight: 1.5,
      },
    ] as CSSInterpolation;

  const linkStyle = withLinkStyle
    ? {
        a: {
          color: styling.colors.camelot,
          borderBottom: `1px solid ${styling.colors.camelot}`,
          lineHeight: '160%',
        },
      }
    : {};

  return {
    ul: [
      markdownListStyle(styling),
      {
        listStyle: 'none',
        li: {
          '::before': {
            content: bulletPoint,
            display: 'inline-block',
            color: color || styling.colors.capeCod,
            width: '1.1em',
          },
        },
      },
    ],
    ol: [markdownListStyle(styling)],
    ...linkStyle,
  };
};

export const getSecondaryButtonStyle = (buildType: BuildType) => {
  switch (buildType) {
    case BuildType.MERIDIAN:
      return {
        whiteBg: ButtonStyle.SECONDARY,
        mobile: ButtonStyle.TRANSPARENT,
        desktop: ButtonStyle.SECONDARY,
      };
    case BuildType.HARBOUR:
      return {
        whiteBg: ButtonStyle.SECONDARY,
        mobile: ButtonStyle.TRANSPARENT,
        desktop: ButtonStyle.TRANSPARENT,
      };
    default:
      return {
        whiteBg: RedesignButtonStyle.TRANSPARENT_WITH_PLUM_FONT,
        mobile: RedesignButtonStyle.WHITE_WITH_PLUM_FONT,
        desktop: RedesignButtonStyle.WHITE_WITH_PLUM_FONT,
      };
  }
};

export type BuildButtonStyle = Partial<{
  showButton: boolean;
  buttonText: string | React.ReactElement;
  buttonLink: string;
  goToGlobal: boolean | null;
  buttonStyle: string | null;
  oldButtonStyle: string;
  newButtonStyle: string;
}>;

export const getBuildButtonStyle = (
  buildType: BuildType,
  button: BuildButtonStyle
): ButtonStyle => {
  if (button.buttonStyle) return button.buttonStyle as ButtonStyle;

  switch (buildType) {
    case BuildType.HARBOUR:
    case BuildType.MERIDIAN:
      return button.oldButtonStyle as ButtonStyle;
    default:
      return button.newButtonStyle as ButtonStyle;
  }
};

export const buttonStyleForGenericRedesignButton = (
  buildType: BuildType,
  button: Queries.GenericRedesignButton
): GeneralRedesignButtonStyle => {
  switch (buildType) {
    case BuildType.MERIDIAN:
      return (button.meridianButtonStyle ?? button.buttonStyle) as RedesignMeridianButtonStyle;
    case BuildType.HARBOUR:
      return (button.harbourButtonStyle ?? button.buttonStyle) as RedesignHarbourButtonStyle;
    default:
      return button.buttonStyle as RedesignButtonStyle;
  }
};

export const getTitleFontColour = (color: TitleFontColour | string | null): string => {
  switch (color) {
    case TitleFontColour.GREY:
      return colors.capeCod;
    case TitleFontColour.PLUM:
    default:
      return colors.camelot;
  }
};

export const getImageSize = (
  size: ImageSize | string | null
): { width: string; height: string } => {
  switch (size) {
    case ImageSize.SMALL:
      return { width: '18.625rem', height: '23.4375rem' };
    case ImageSize.LARGE:
    default:
      return { width: '26.5rem', height: '34.625rem' };
  }
};

export const getTextBoxPrams = (
  width: ProductBannerImageWithTextStyle | string | null,
  textOverPosition: string | null,
  isMeridian: boolean
): { xs: number; lg: number; xl: number; xlOffset: number; lgOffset?: number } => {
  switch (width) {
    case ProductBannerImageWithTextStyle.WIDE:
      return textOverPosition === TextOverPosition.LEFT
        ? { xs: 12, lg: 7, xl: 6, xlOffset: 1 }
        : { xs: 12, lg: 7, xl: 6, lgOffset: 5, xlOffset: 6 };
    case ProductBannerImageWithTextStyle.THIN:
    default:
      return textOverPosition === TextOverPosition.LEFT
        ? { xs: 12, lg: 6, xl: isMeridian ? 5 : 4, xlOffset: 1 }
        : { xs: 12, lg: 6, xl: isMeridian ? 5 : 4, lgOffset: 6, xlOffset: 7 };
  }
};

export const buttonStyles = (align?: string | null) => {
  switch (align) {
    case ButtonsArrangement.VERTICAL:
      return {
        flexDirection: ['column', 'column'],
        buttonGap: { marginBottom: ['1.19rem', '1.19rem'] },
      };
    case ButtonsArrangement.HORIZONTAL:
    default:
      return {
        flexDirection: ['column', 'row'],
        buttonGap: { margin: ['0 0 1.19rem 0', '0 1.5rem 0 0'] },
      };
  }
};

export const getBannerTexColorByTheme = (
  styling: Styling,
  theme: BannerTextBackgroundTheme | string | null
): { background: string; color: string; markdownColor: string } => {
  switch (theme) {
    case BannerTextBackgroundTheme.BLUE:
      return {
        background: colors.meridianBlue,
        color: styling.colors.lightBackground,
        markdownColor: styling.colors.lightBackground,
      };
    case BannerTextBackgroundTheme.LIGHT:
    default:
      return {
        background: styling.colors.lightBackground,
        color: colors.meridianBlue,
        markdownColor: styling.colors.capeCod,
      };
  }
};

export const getBackgroundTheme = (
  styling: Styling,
  theme: WidgetTheme | string | null,
  buildType: BuildType,
  alpha?: number
): {
  background: string | undefined;
  titleColor: string;
  textColor: string;
  rightSideColor: string;
  rightSideBorderColor: string;
  listItemBorderColor: string;
} => {
  const getTitleColor = (buildType: BuildType) => {
    if (buildType === BuildType.MERIDIAN) return styling.colors.meridianOrange;
    if (buildType === BuildType.HARBOUR) return styling.colors.harbourSlateGrey;
    return styling.colors.camelot;
  };
  switch (theme) {
    case WidgetTheme.BLUE:
      return {
        background: colors.meridianBlue,
        titleColor: styling.colors.lightBackground,
        textColor: styling.colors.lightBackground,
        rightSideBorderColor: styling.colors.lightBackground,
        rightSideColor: styling.colors.lightBackground,
        listItemBorderColor: styling.colors.lightBackground,
      };
    case WidgetTheme.DARK:
      return {
        background: styling.colors.outerSpace,
        titleColor: styling.colors.pineGlade,
        textColor: styling.colors.pineGlade,
        rightSideBorderColor: styling.colors.greenSmoke,
        rightSideColor: styling.colors.blackHaze,
        listItemBorderColor: styling.colors.edward,
      };
    case WidgetTheme.GOLD:
      return {
        background: styling.colors.paleGold,
        titleColor: styling.colors.harbourSlateGrey,
        textColor: styling.colors.capeCod,
        rightSideBorderColor: styling.colors.harbourGold,
        rightSideColor: styling.colors.capeCod,
        listItemBorderColor: styling.colors.harbourGold,
      };
    case WidgetTheme.LIGHT:
    default:
      return {
        background: rgbaValues(styling.colors.sageBackground, alpha),
        titleColor: getTitleColor(buildType),
        textColor: styling.colors.capeCod,
        rightSideBorderColor: styling.colors.grayOlive,
        rightSideColor: styling.colors.capeCod,
        listItemBorderColor: styling.colors.ash,
      };
  }
};

export const iconTheme = (
  styling: Styling,
  isMeridian: boolean,
  theme?: IconTheme | string | null
) => {
  if (isMeridian) return styling.colors.meridianBlue;
  switch (theme) {
    case IconTheme.GREEN:
      return styling.colors.darkGreen;
    case IconTheme.GREY:
      return styling.colors.capeCod;
    case IconTheme.PLUM:
    default:
      return styling.colors.camelot;
  }
};
